import type { ComponentType } from 'react';
import { Award, Lightbulb, Package } from 'lucide-react';
import {
  Activity,
  AppWindow,
  BellRing,
  Bot,
  Building2,
  CalendarClock,
  CreditCard,
  FileClock,
  Gauge,
  Gift,
  Globe2,
  KeyRound,
  LayoutDashboard,
  LifeBuoy,
  LockKeyhole,
  Mail,
  MailCheck,
  Megaphone,
  Palette,
  Plug,
  ReceiptText,
  ScrollText,
  Settings2,
  ShieldAlert,
  ShieldBan,
  ShieldCheck,
  TicketPercent,
  UserRoundSearch,
  Users,
  Webhook,
  Wrench,
  type LucideProps,
} from 'lucide-react';

export interface PlatformNavItem {
  to: string;
  label: string;
  icon: ComponentType<LucideProps>;
  /** Extra words matched by the sidebar module search. */
  keywords?: string;
  end?: boolean;
}

export interface PlatformNavGroup {
  label: string;
  items: PlatformNavItem[];
}

/** Every Super Admin module, grouped by job. Routes are wired in App.tsx; keep the two in sync. */
export const PLATFORM_NAV: PlatformNavGroup[] = [
  {
    label: 'Overview',
    items: [{ to: '/platform', label: 'Dashboard', icon: LayoutDashboard, end: true, keywords: 'overview kpi mrr' }],
  },
  {
    label: 'Tenants & users',
    items: [
      { to: '/platform/tenants', label: 'Tenants', icon: Building2, keywords: 'companies workspaces agencies organisations' },
      { to: '/platform/users', label: 'Users', icon: Users, keywords: 'people accounts' },
      { to: '/platform/support-access', label: 'Support access', icon: LifeBuoy, keywords: 'grants support' },
      { to: '/platform/support-tickets', label: 'Support tickets', icon: LifeBuoy, keywords: 'help requests organisation support' },
      { to: '/platform/impersonation', label: 'Impersonation', icon: UserRoundSearch, keywords: 'login as act as' },
    ],
  },
  {
    label: 'Revenue',
    items: [
      { to: '/platform/plans', label: 'Plans', icon: CreditCard, keywords: 'packages limits pricing' },
      { to: '/platform/billing', label: 'Billing', icon: ReceiptText, keywords: 'subscriptions trials renewals payments invoices' },
      { to: '/platform/coupons', label: 'Coupons', icon: TicketPercent, keywords: 'discount promo code' },
      { to: '/platform/referrals', label: 'Referrals', icon: Gift, keywords: 'referral programme reward credit' },
      { to: '/platform/countries', label: 'Countries', icon: Globe2, keywords: 'country packages region currency' },
    ],
  },
  {
    label: 'Communication',
    items: [
      { to: '/platform/notifications', label: 'Notifications', icon: BellRing, keywords: 'inbox unread all workspaces recipients' },
      { to: '/platform/announcements', label: 'Announcements', icon: Megaphone, keywords: 'notification banner broadcast' },
      { to: '/platform/email', label: 'Email', icon: Mail, keywords: 'smtp sender provider master templates' },
      { to: '/platform/email-logs', label: 'Email logs', icon: MailCheck, keywords: 'sent delivery date time' },
      { to: '/platform/inactivity-reminders', label: 'Inactivity reminders', icon: CalendarClock, keywords: 'inactive users' },
      { to: '/platform/usage-reports', label: 'Usage reports', icon: FileClock, keywords: 'usage scheduled report email' },
    ],
  },
  {
    label: 'Security',
    items: [
      { to: '/platform/audit', label: 'Audit', icon: ScrollText, keywords: 'audit logs activity history' },
      { to: '/platform/security', label: 'Security', icon: ShieldCheck, keywords: 'two factor 2fa mfa sessions policy' },
      { to: '/platform/login-alerts', label: 'Login alerts', icon: BellRing, keywords: 'login alerts email video new device' },
      { to: '/platform/google-login', label: 'Google/X login', icon: LockKeyhole, keywords: 'google twitter x social sign-in login oauth sso authentication' },
      { to: '/platform/ip-blocker', label: 'IP blocker', icon: ShieldBan, keywords: 'allowlist denylist cidr' },
      { to: '/platform/maintenance', label: 'Maintenance', icon: Wrench, keywords: 'maintenance mode downtime' },
      { to: '/platform/ai/firewall', label: 'AI firewall', icon: ShieldAlert, keywords: 'policy limits redaction' },
    ],
  },
  {
    label: 'AI',
    items: [
      { to: '/platform/ai/providers', label: 'Providers', icon: Bot, keywords: 'ai providers modules claude anthropic openai gemini default model limit fallback' },
      { to: '/platform/ai/keys', label: 'Keys', icon: KeyRound, keywords: 'ai api keys rotation priority fallback user-owned' },
      { to: '/platform/ai/usage', label: 'Usage', icon: Gauge, keywords: 'ai usage tokens cost per user company' },
    ],
  },
  {
    label: 'System',
    items: [
      { to: '/platform/settings', label: 'Settings', icon: Settings2, keywords: 'platform settings general feature flags limits storage regional signup' },
      { to: '/platform/appearance', label: 'Appearance', icon: AppWindow, keywords: 'appearance modules font size header navigation' },
      { to: '/platform/white-label', label: 'White-label', icon: Palette, keywords: 'branding logo favicon custom domain' },
      { to: '/platform/connectors', label: 'Connectors', icon: Plug, keywords: 'integrations health test connection' },
      { to: '/platform/api', label: 'API', icon: Webhook, keywords: 'api keys endpoints webhooks deliveries' },
      { to: '/platform/health', label: 'Health', icon: Activity, keywords: 'system health monitoring uptime queue worker' },
    ],
  },
];

PLATFORM_NAV.push({ label: 'More', items: [
  { to: '/platform/more/products', label: 'Our Other Products', icon: Package },
  { to: '/platform/more/suggest-feature', label: 'Feature suggestions', icon: Lightbulb },
  { to: '/platform/more/gamification', label: 'Badges & XP', icon: Award },
] });

export const PLATFORM_NAV_ITEMS: PlatformNavItem[] = PLATFORM_NAV.flatMap((g) => g.items);

export function filterPlatformNav(query: string): PlatformNavGroup[] {
  const q = query.trim().toLowerCase();
  if (!q) return PLATFORM_NAV;
  return PLATFORM_NAV.map((g) => ({ ...g, items: g.items.filter((i) => `${i.label} ${i.keywords ?? ''} ${g.label}`.toLowerCase().includes(q)) })).filter((g) => g.items.length > 0);
}
